import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Chart } from "chart.js/auto";
import { AssetReviewService } from "../asset-review/asset-review.service";

export interface DonutChartData {
  name: string;
  percentage: number;
  color: string;
}

interface GradeWeightage {
  id: number;
  sectionName: string;
  weightage: number;
}

/**
 * Section weightings are reference data shared by every store, so they are
 * cached under a single key rather than per store uuid.
 */
const WEIGHTAGE_CACHE_KEY = "overall-grades-breakdown";

/**
 * Maps `section_name` as stored by the backend onto the label and colour used
 * in the donut. The order here is the order the slices are drawn in.
 */
const SECTIONS: { sectionName: string; name: string; color: string }[] = [
  { sectionName: "Total Sales", name: "Sales", color: "#22C55E" },
  { sectionName: "Sales Per SQFT", name: "SPSF", color: "#84CC16" },
  { sectionName: "Real Estate Quality", name: "Real Estate", color: "#FDE047" },
  { sectionName: "Banner Strength", name: "Banner", color: "#FACC15" },
  { sectionName: "Competitive Impact", name: "Competitive Impact", color: "#F87171" }
];

/** Current values in mtn_asset_review.overall_banner_grades_breakdown. */
const DEFAULT_WEIGHTAGES: Record<string, number> = {
  "Total Sales": 38.75,
  "Sales Per SQFT": 38.75,
  "Real Estate Quality": 7.5,
  "Banner Strength": 7.5,
  "Competitive Impact": 7.5
};

@Component({
  selector: "app-grade-breakdown-content",
  templateUrl: "./grade-breakdown-content.component.html",
  styleUrls: ["./grade-breakdown-content.component.scss"],
  standalone: true,
  imports: [CommonModule],
})
export class GradeBreakdownContentComponent implements OnInit,OnChanges, AfterViewInit {
  @ViewChild("chartCanvas", { static: false })
  chartCanvas!: ElementRef<HTMLCanvasElement>;
  chart: any;
  chartData: DonutChartData[] = SECTIONS.map(section => ({
    name: section.name,
    percentage: DEFAULT_WEIGHTAGES[section.sectionName],
    color: section.color,
  }));
  @Input() isFullScreen: boolean = false;
  @Input() isNewPage: boolean = false;
  constructor(private assetReviewService: AssetReviewService) {}

  ngOnInit() {
    this.loadWeightages();
  }

  private loadWeightages(): void {
    this.assetReviewService.assetReviewGradeBreakdownChart(WEIGHTAGE_CACHE_KEY).subscribe({
      next: (weightages: GradeWeightage[]) => {
        if (!Array.isArray(weightages) || !weightages.length) {
          return;
        }

        const bySection = new Map<string, number>(
          weightages.map(weightage => [
            this.normalizeSectionName(weightage.sectionName),
            Number(weightage.weightage),
          ])
        );

        this.chartData = SECTIONS.map(section => {
          const weightage = bySection.get(this.normalizeSectionName(section.sectionName));

          return {
            name: section.name,
            percentage:
              weightage === undefined || isNaN(weightage)
                ? DEFAULT_WEIGHTAGES[section.sectionName]
                : weightage,
            color: section.color,
          };
        });

        this.updateChartData();
      },
      // Leave the defaults in place if the lookup fails.
      error: () => {},
    });
  }

  private normalizeSectionName(sectionName: string): string {
    return (sectionName || "").trim().toLowerCase();
  }

  private updateChartData(): void {
    if (!this.chart) {
      return;
    }

    this.chart.data.labels = this.chartData.map(data => data.name);
    this.chart.data.datasets[0].data = this.chartData.map(data => data.percentage);
    this.chart.data.datasets[0].backgroundColor = this.chartData.map(data => data.color);
    this.chart.update();
  }

  ngOnChanges(changes: SimpleChanges): void {
      console.log("🚀 ~ GradeBreakdownContentComponent ~ ngOnChanges ~ changes:", changes)

  }

  ngAfterViewInit() {
    this.createDoughnutChart();
  }

  createDoughnutChart() {
    const ctx = this.chartCanvas.nativeElement.getContext("2d");

    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: this.chartData.map((data) => data.name),
        datasets: [
          {
            data: this.chartData.map((data) => data.percentage),
            backgroundColor: this.chartData.map((data) => data.color),
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '50%',
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            callbacks: {
              label: function (tooltipItem: any) {
                const label = tooltipItem.label;
                const value = tooltipItem.raw;
                return `${label}: ${value}%`;
              },
            },
          },
        },
      } as any,
    });
  }
}
